import { usePersistedSearchParam } from '@/hooks/usePersistedSearchParam';
import { STORAGE_KEYS } from '@/libs/storage';

export const WORKSPACE_PARAM = 'workspace';

export interface UseActiveWorkspaceResult {
  /** The active workspace id from `?workspace=<id>`, or `null` when absent. */
  activeId: string | null;
  /** Writes the workspace param to the URL, preserving all other params. */
  setActive: (id: string) => void;
  /** Removes the workspace param from the URL, preserving all other params. */
  clearActive: () => void;
}

/**
 * Derives the active workspace id from the `?workspace=<id>` URL query param.
 * The URL is the single source of truth — no client store. The active
 * workspace is view-state, so `setActive`/`clearActive` history-replace (no
 * back-button entry). Backed by `usePersistedSearchParam`: restores the
 * last-used workspace from `localStorage` on a cold mount with no param, and
 * persists/clears it as the active id changes.
 */
export function useActiveWorkspace(): UseActiveWorkspaceResult {
  const { value, set, clear } = usePersistedSearchParam({
    param: WORKSPACE_PARAM,
    storageKey: STORAGE_KEYS.LAST_WORKSPACE_ID,
  });

  return { activeId: value, setActive: set, clearActive: clear };
}
